/**
 * TOSS Web Application — Client portal: legal-entity switcher (release Р4, decision Р20)
 *
 * `ClientHeader`'s company control — see
 * `vault/specs/client-portal/prep-work/r4-company-switch-rewrites-the-session-not-the-token.md`
 * §6.
 *
 * Three placements, one component, so the open/close/pending/error behaviour lives in one
 * place instead of three components that could drift apart on it:
 *
 *  - `inline` — beside the company facts on the profile screen (≥768);
 *  - `header` — the computer's top bar (≥768), where since 2026-08-26 the company is the ONLY
 *    thing on the left: the section name went back to being the page's business, so this is the
 *    bar's heading and, for an account with a second legal entity, the control that switches it.
 *    Same role as `appbar` one breakpoint up, and the same rule — it renders for a
 *    single-company account too, because a bar that named the cabinet for almost nobody would
 *    leave every number below it unattributed;
 *  - `stacked` — under the title on the phone in the old header (≤767);
 *  - `appbar` — the phone app bar's TITLE (≤767). This one renders the company name even
 *    for an account with a single company, because there it is not a control, it is the
 *    heading: an app bar has to say whose cabinet this is. The chevron, the dropdown and
 *    the unread badge appear only when there is a second company to switch to, so the
 *    ordinary single-company account still sees no control it cannot use.
 *  - `chip` — the same thing shrunk to a chip, for `ClientSectionAppBar` (≤767), where the
 *    SECTION name is the heading and the company sits beside the bell. It keeps `appbar`'s
 *    rule of rendering for a single-company account too — that bar has no other line
 *    saying whose cabinet this is — but as muted text rather than an `<h1>`: the screen
 *    already has its heading, and a second one would make two.
 *
 * In `inline` and `stacked` the component renders NOTHING for a single-company account —
 * that account must see exactly what it saw before the switcher shipped (contract §7
 * acceptance #1), and neither `appbar` nor `chip` weakens that: they add a label, not a
 * control.
 *
 * Money is never in scope here. What the control carries per company is a name, an id and an
 * unread COUNT — decision B7 amendment 1 (`r4-contract-decisions.md`). The count is the
 * reason this component is more than a convenience: notification recipients resolve through
 * company membership, so an employee of А and Б receives А's approval while acting as Б, and
 * every read path then hides it from them on purpose (the feed is scoped to the active
 * company, marking it read answers 404, the live push is suppressed). Without the number an
 * additional-work approval — the thing a dispatcher is on the phone waiting for — would be
 * discoverable only by switching legal entity on a hunch.
 *
 * So the number appears twice, and both are deliberate: on each row of the open list
 * («Евросиб · 3»), and on the CLOSED trigger as the total for the other companies. A counter
 * visible only inside an opened dropdown is not a signal — nothing would tell the customer to
 * open it. Neither surface says anything beyond «how many»: no headline, no application
 * number, no sum, no deep link. Reading the other company's notifications still requires
 * switching to it, which is what makes every scoped read on the backend still the gate.
 */

import { useRef, useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import type { ClientCompanyRefDto } from '@/api/clientPortal/clientCompany.api';
import { useClientProfile } from '@/hooks/api/clientPortal/useClientProfile';
import { DropdownPortal, Spinner } from '@/components/ui';
import { useClientCompanySwitchHandler } from './useClientCompanySwitchHandler';
import { companyInitials } from './clientCompanyInitials';

// ============================================================================
// Types
// ============================================================================

export type ClientCompanySwitcherPlacement =
  | 'inline'
  | 'stacked'
  | 'appbar'
  | 'chip'
  | 'header'
  | 'sidebar'
  | 'sidebar-collapsed';

export interface ClientCompanySwitcherProps {
  /** Which of the two header layouts this instance renders — see the file docblock. */
  placement: ClientCompanySwitcherPlacement;
}

// ============================================================================
// Component
// ============================================================================

export function ClientCompanySwitcher({
  placement,
}: ClientCompanySwitcherProps): JSX.Element | null {
  const { showMobileNav } = useResponsive();
  const { data: profile } = useClientProfile();
  // Shared with the profile screen's own company row — see the hook for what must not
  // differ between the two controls.
  const { switchTo, isPending } = useClientCompanySwitchHandler();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isChip = placement === 'chip';
  // The computer's sidebar block, under the logo: a full-width tinted row rather than a chip in
  // a header that no longer exists there — the section name and the company were two headings
  // competing for one role, which is why the header went away at ≥768.
  const isSidebar = placement === 'sidebar';
  // The same block in a 64 px column (tablet 768–1023, or a desktop sidebar the customer folded
  // away). Added 2026-08-24: between 768 and 1023 the switcher had no place on the screen at all
  // — the phone's app bar does not render there, the expanded sidebar row is hidden while
  // collapsed, and Р-desktop removed the header. An account with three legal entities could only
  // switch by going into «Профиль», three taps away from the list it wanted to re-read.
  const isSidebarCollapsed = placement === 'sidebar-collapsed';
  // Both render a LABEL for the single-company account rather than disappearing; the
  // difference between them is typographic, so the branches below share this flag.
  const isAppBar = placement === 'appbar' || isChip;
  // The computer header's own line (2026-08-26, replacing the second-line `subtitle` that sat
  // under a section name this bar no longer prints). Like `appbar` it is a LABEL first and a
  // control second, so it renders for the single-company account too — unlike `inline`, which
  // is a control and correctly disappears when there is nothing to switch.
  const isHeader = placement === 'header';
  const isPhonePlacement = placement === 'stacked' || isAppBar;
  const isOwnBreakpoint = isPhonePlacement === showMobileNav;

  // Both instances call the same cached query and mutation regardless — cheaper and
  // simpler than threading the result through props from a third, wrapping component, and
  // TanStack dedupes the two `useClientProfile()` observers into one request either way
  // (the same pattern `useClientNavBadges` already relies on for the two badge counters).
  if (!isOwnBreakpoint || !profile) {
    return null;
  }

  const canSwitch = profile.availableCompanies.length > 1;

  // `inline` / `stacked` are controls and disappear when there is nothing to control.
  // `appbar` and `header` are headings that happen to be controls, and stay — see the docblock.
  if (!canSwitch && !isAppBar && !isHeader) {
    return null;
  }

  // The heading with no control behind it: a single-company account. Rendered as plain
  // text, not a disabled button — a chevron that opens a list of one is a promise the UI
  // cannot keep, and a disabled control invites a tap that does nothing.
  if (!canSwitch) {
    // 64 px has no room for a name, and a circle of initials that opens nothing would invite a
    // click that does nothing. The company is already in the account block's tooltip below.
    if (isSidebarCollapsed) return null;

    // In `chip` the screen's own `<h1>` is two elements away, so this is a plain label:
    // two headings on one bar would make the company compete with the section name for
    // the same role.
    if (isSidebar) {
      return (
        <span className="block text-sm font-medium text-[var(--color-text-primary)] truncate">
          {profile.customerName}
        </span>
      );
    }

    // The computer bar's heading. Nothing else is printed on that side any more, so this is an
    // `<h1>` exactly as the phone's app bar is — the two bars now say the same thing in the same
    // role, one at 18 px and one at 16.
    if (isHeader) {
      return (
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
          {profile.customerName}
        </h1>
      );
    }

    return isChip ? (
      <span className="block text-xs text-[var(--color-text-secondary)] truncate">
        {profile.customerName}
      </span>
    ) : (
      <h1 className="text-base font-semibold text-[var(--color-text-primary)] truncate">
        {profile.customerName}
      </h1>
    );
  }

  // Only the OTHER companies. The active company's unread is already the bell badge two
  // elements away, and counting it here would light the switcher up for something the
  // customer can see and act on without touching this control at all.
  const otherCompaniesUnread = profile.availableCompanies
    .filter((company) => company.id !== profile.customerId)
    .reduce((total, company) => total + company.unreadCount, 0);

  const handleSelect = (company: ClientCompanyRefDto): void => {
    switchTo(company.id, profile.customerId, () => setIsOpen(false));
  };

  const triggerClassName = isSidebarCollapsed
    ? // A round marker with the company's initials, sized and tinted like the nav rows beside it.
      // No chevron and no name: neither fits, and the tooltip carries the full name anyway.
      'relative mx-auto flex items-center justify-center w-10 h-10 rounded-full text-xs font-medium bg-[var(--color-primary-50)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-100)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
    : isSidebar
    ? // Full width of the sidebar, tinted like the chip: the customer's own company is the
      // first thing the screen says, and for a two-entity account it is also the control that
      // decides what everything below it means.
      'flex items-center gap-1.5 w-full min-w-0 rounded-[var(--radius-md)] px-2.5 py-2 text-sm font-medium bg-[var(--color-primary-50)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-100)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
    : isHeader
      ? // Same size and weight as the single-company heading above, so the account with two legal
        // entities reads the SAME sentence the account with one reads — plus a chevron.
        'flex items-center gap-1.5 min-w-0 max-w-full -ml-1 rounded-[var(--radius-lg)] px-1 py-0.5 text-lg font-semibold text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
    : placement === 'inline'
      ? 'flex items-center gap-1.5 min-w-0 max-w-[220px] -ml-2 rounded-[var(--radius-lg)] px-2 py-1.5 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
      : isChip
        ? // A tinted pill beside the section name. Small on purpose — it is orientation
          // plus a control, not the subject of the screen — but tinted rather than muted
          // grey, so an account with two legal entities can still find the thing that
          // switches them without hunting.
          'flex items-center gap-1 min-w-0 max-w-full rounded-full px-2.5 py-1 text-xs font-medium bg-[var(--color-primary-50)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-100)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
        : isAppBar
        ? // The app bar's heading, and a control at the same time: same size and weight as
          // a plain page title so the bar reads as a title bar, with the chevron as the only
          // hint that this one can be tapped.
          'flex items-center gap-1.5 min-w-0 max-w-full -ml-1 rounded-[var(--radius-lg)] px-1 py-0.5 text-base font-semibold text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
        : 'flex items-center gap-1 min-w-0 max-w-full text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-60 transition-colors';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={
          otherCompaniesUnread > 0
            ? `Юридическое лицо: ${profile.customerName}. Открыть список. Непрочитанных в других компаниях: ${otherCompaniesUnread}`
            : `Юридическое лицо: ${profile.customerName}. Открыть список`
        }
      >
        {placement === 'inline' && (
          <Building2
            className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]"
            aria-hidden="true"
          />
        )}

        {isSidebarCollapsed ? (
          <>
            {isPending ? (
              <Spinner size="xs" />
            ) : (
              <span aria-hidden="true">{companyInitials(profile.customerName)}</span>
            )}
            {otherCompaniesUnread > 0 && (
              // A dot, not a number — the same call `ClientNavRow` makes when collapsed: a digit
              // clipped by a 40 px circle reads worse than no digit at all.
              <span
                data-testid="client-company-switcher-other-unread"
                className="absolute top-0 right-0 w-2 h-2 rounded-full bg-[var(--color-primary-500)] ring-2 ring-[var(--color-bg-card)]"
                aria-hidden="true"
              />
            )}
          </>
        ) : (
          <>
            <span className="truncate">{profile.customerName}</span>
            {otherCompaniesUnread > 0 && (
              <span
                data-testid="client-company-switcher-other-unread"
                className="flex-shrink-0 text-3xs font-bold leading-none text-white rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 bg-[var(--color-primary-500)]"
                aria-hidden="true"
              >
                {otherCompaniesUnread > 99 ? '99+' : otherCompaniesUnread}
              </span>
            )}
            {isPending ? (
              <Spinner size="xs" className="flex-shrink-0" />
            ) : (
              <ChevronDown
                className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            )}
          </>
        )}
      </button>

      <DropdownPortal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
        // Aligned to the trigger's RIGHT edge in the section app bar, where the chip itself
        // sits at the right of the bar: `bottom-start` pinned the list's LEFT edge to the
        // chip's, which on a 375 px screen pushed a 256 px list off the right side — the
        // portal then shunted it back inside, and it landed under the middle of the screen
        // looking unattached to anything. `inline` keeps left alignment: there the trigger
        // is the leftmost thing in the header and a right-aligned list would drift.
        //
        // Привязка к триггеру больше не опция: `keepAnchoredOnNarrow` был опт-аутом из ветки
        // «ниже 480 px растянуть на весь экран», а та ветка удалена — портал крепится к
        // триггеру на любой ширине. Решение PO при этом в силе и просто перестало требовать
        // флага: список висит под чипом, а не панелью поперёк экрана.
        placement={isChip ? 'bottom-end' : 'bottom-start'}
        className="w-64 max-w-[calc(100vw-2rem)] py-1 app-surface app-surface-outlined shadow-[var(--shadow-overlay)]"
      >
        <p className="px-3 pt-1.5 pb-1 text-xs font-medium text-[var(--color-text-secondary)]">
          Юридическое лицо
        </p>
        <ul role="listbox" aria-label="Юридическое лицо">
          {profile.availableCompanies.map((company) => {
            const isActive = company.id === profile.customerId;
            return (
              <li key={company.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  disabled={isPending}
                  onClick={() => handleSelect(company)}
                  // An explicit accessible name rather than the rendered text, so the count
                  // is spoken as «непрочитанных: 3» instead of a bare digit trailing the
                  // company name — and so a company with nothing waiting is named by its
                  // name alone.
                  aria-label={
                    company.unreadCount > 0
                      ? `${company.name}, непрочитанных: ${company.unreadCount}`
                      : company.name
                  }
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="flex-1 min-w-0 truncate">{company.name}</span>
                  {company.unreadCount > 0 && (
                    <span
                      className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-full text-white min-w-[18px] text-center bg-[var(--color-primary-500)]"
                      aria-hidden="true"
                    >
                      {company.unreadCount > 99 ? '99+' : company.unreadCount}
                    </span>
                  )}
                  {isActive && (
                    <Check
                      className="w-4 h-4 flex-shrink-0 text-[var(--color-primary-600)]"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </DropdownPortal>
    </>
  );
}

export default ClientCompanySwitcher;
